/**
 * Descriptor presentation hints (contracts `ui`): Markdown bodies, lifecycle header, deadline
 * urgency. Generic: any module whose descriptor declares the hint gets the behaviour.
 */
import React from "react";
import { MarkdownField } from "@refinedev/antd";
import { Steps, Tag, Tooltip } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { UiConfig } from "@evo-flywheel/contracts";
import { MarkdownEditor } from "@evo-flywheel/ui";
import { resourceBySlug } from "../kit-resources";

export const uiOf = (slug?: string): UiConfig => (resourceBySlug[slug ?? ""]?.meta.ui ?? {}) as UiConfig;
export const isMarkdown = (slug: string | undefined, field: string) => !!uiOf(slug).markdown?.includes(field);

export const MarkdownView: React.FC<{ value: unknown }> = ({ value }) =>
  <div className="cmp-markdown"><MarkdownField value={String(value ?? "")} /></div>;

/** Form control for a Markdown field: write / preview tabs (value + onChange from Form.Item). */
/** Markdown body editor: the shared @evo-flywheel/ui MarkdownEditor (toolbar, shortcuts, side-by-side preview). */
export const MarkdownInput: React.FC<{ value?: string; onChange?: (v: string) => void }> = (props) => <MarkdownEditor {...props} />;

/** Italian display of stored picklist values (the stored value never changes; UI contract rule 11). */
const VALUE_IT: Record<string, string> = {
  accepted: "Accettata", "final acceptance": "Accettazione definitiva", pending: "In attesa", read: "Letta", rejected: "Rifiutata",
  draft: "Bozza", active: "In vigore", published: "Pubblicata", archived: "Archiviata",
  planned: "Pianificato", in_progress: "In corso", completed: "Completato", implemented: "Implementata", tbd: "Da valutare",
  open: "Aperto", closed: "Chiuso", assessed: "Valutato", not_required: "Non richiesto", failed: "Non superato",
  critical: "Critico", important: "Importante", standard: "Standard", high: "Alto", medium: "Medio", low: "Basso",
  risk: "Rischio", unlikely: "Improbabile", manual: "Manuale", "firma digital": "Firma digitale", portal: "Self-service",
};
export const valueLabel = (value: string) => VALUE_IT[value.toLowerCase()] ?? value.replace(/_/g, " ");

/** Lifecycle header: the record's status placed on its ordered steps. */
export const Lifecycle: React.FC<{ slug?: string; record: Record<string, unknown> }> = ({ slug, record }) => {
  const lc = uiOf(slug).lifecycle;
  if (!lc) return null;
  const value = String(record[lc.field] ?? "");
  const at = lc.steps.indexOf(value);
  return <Steps size="small" style={{ marginBottom: 16 }} current={at < 0 ? 0 : at} status={at < 0 ? "wait" : "process"}
    items={lc.steps.map((s) => ({ title: valueLabel(s) }))} />;
};

/** Urgency of a deadline: overdue (red), within the first / second threshold (orange / gold), later (green). */
export function urgency(value: unknown, warnDays: number[] = [30, 90]): { color: string; label: string } | undefined {
  if (!value) return undefined;
  const d = dayjs(String(value));
  if (!d.isValid()) return undefined;
  const hours = d.diff(dayjs(), "hour");
  const days = Math.floor(hours / 24);
  const span = Math.abs(days) >= 1 ? `${Math.abs(days)} gg` : `${Math.abs(hours)} h`;
  if (hours < 0) return { color: "red", label: `scaduta da ${span}` };
  if (days <= warnDays[0]) return { color: "orange", label: `tra ${span}` };
  if (days <= (warnDays[1] ?? warnDays[0])) return { color: "gold", label: `tra ${span}` };
  return { color: "green", label: `tra ${span}` };
}

export const deadlineOf = (slug: string | undefined, field: string) => uiOf(slug).deadlines?.find((d) => d.field === field);

export const DeadlineTag: React.FC<{ value: unknown; warnDays?: number[] }> = ({ value, warnDays }) => {
  const u = urgency(value, warnDays);
  if (!u) return null;
  return <Tooltip title={dayjs(String(value)).format("DD/MM/YYYY HH:mm")}><Tag icon={<ClockCircleOutlined />} color={u.color}>{u.label}</Tag></Tooltip>;
};

/** All deadlines of a record, for the Show header. */
export const Deadlines: React.FC<{ slug?: string; record: Record<string, unknown>; labelOf: (f: string) => string }> = ({ slug, record, labelOf }) => {
  const ds = (uiOf(slug).deadlines ?? []).filter((d) => record[d.field]);
  if (!ds.length) return null;
  return <div style={{ marginBottom: 16 }}>{ds.map((d) => <span key={d.field} style={{ marginRight: 12 }}>
    {labelOf(d.field)}: <DeadlineTag value={record[d.field]} warnDays={d.warnDays} />
  </span>)}</div>;
};
